import { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDrop } from 'react-dnd';
import { Empty, message } from 'antd';
import useDashboardStore from '../../stores/dashboardStore';
import usePluginStore from '../../plugins/plugin-manager';
import ChartRenderer from '../charts/ChartRenderer';
import { resolveBoxShadow } from './shadowUtils';
import { makeWidgetClickHandler } from '../../core/widgetEventDispatch';

const MIN_SIZE = 40;
const SELECT_COLOR = '#1677ff';
const SNAP_DIST = 5; // 对齐辅助线吸附阈值(px)

export interface CanvasView {
  scaleX: number;
  scaleY: number;
}

export interface Guide {
  axis: 'v' | 'h';
  pos: number;
}

// 读取组件布局/样式：优先 base(四段式)，回退 config/style 兼容旧数据
function readBase(comp: any, key: string, fallback: unknown) {
  if (comp?.base && comp.base[key] !== undefined) return comp.base[key];
  if (comp?.config && comp.config[key] !== undefined) return comp.config[key];
  if (comp?.style && comp.style[key] !== undefined) return comp.style[key];
  return fallback;
}

function snapToGrid(v: number, grid: number): number {
  if (grid <= 1) return Math.round(v);
  return Math.round(v / grid) * grid;
}

function ResizeHandle({ direction, onResize }: { direction: string; onResize: (dx: number, dy: number) => void }) {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const handleMouseMove = (ev: MouseEvent) => onResize(ev.clientX - startX, ev.clientY - startY);
    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const style: React.CSSProperties = {
    position: 'absolute',
    width: 22,
    height: 22,
    background: '#fff',
    border: `1.5px solid ${SELECT_COLOR}`,
    zIndex: 12,
    borderRadius: 3,
    boxShadow: '0 0 4px rgba(0,0,0,0.4)',
    // 视觉中心 10px（内圆），整体 22px 点击热区
    boxSizing: 'border-box',
    cursor: direction.includes('n') && direction.includes('w') ? 'nwse-resize'
      : direction.includes('n') && direction.includes('e') ? 'nesw-resize'
      : direction.includes('s') && direction.includes('w') ? 'nesw-resize'
      : direction.includes('s') && direction.includes('e') ? 'nwse-resize'
      : direction === 'n' || direction === 's' ? 'ns-resize'
      : 'ew-resize',
  };

  if (direction.includes('n')) style.top = -11;
  if (direction.includes('s')) style.bottom = -11;
  if (direction.includes('w')) style.left = -11;
  if (direction.includes('e')) style.right = -11;

  return <div style={style} onMouseDown={handleMouseDown} />;
}

function RotateHandle({ onRotate }: { onRotate: (deg: number) => void }) {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const box = (e.currentTarget as HTMLElement).closest('[data-comp-box]') as HTMLElement;
    const rect = box?.getBoundingClientRect();
    const cx = rect ? rect.left + rect.width / 2 : 0;
    const cy = rect ? rect.top + rect.height / 2 : 0;
    const start = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
    const handleMove = (ev: MouseEvent) => {
      const cur = Math.atan2(ev.clientY - cy, ev.clientX - cx) * (180 / Math.PI);
      onRotate(cur - start);
    };
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      title="旋转"
      style={{
        position: 'absolute', top: -22, left: '50%', marginLeft: -7,
        width: 14, height: 14, borderRadius: '50%', zIndex: 12,
        background: SELECT_COLOR, border: '2px solid #fff',
        cursor: 'grab', boxSizing: 'border-box',
      }}
    />
  );
}

function DraggableComponent({
  comp,
  isSelected,
  scaleX,
  scaleY,
  snap = 1,
  canvasSize,
  onSelect,
  onMove,
  onResize,
  onRotate,
  onRemove,
  onGuidesChange,
  onContextMenu,
}: {
  comp: any;
  isSelected: boolean;
  scaleX: number;
  scaleY: number;
  snap?: number;
  canvasSize: { width: number; height: number };
  onSelect: (e: React.MouseEvent) => void;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, width: number, height: number) => void;
  onRotate: (id: string, deg: number) => void;
  onRemove: (id: string) => void;
  onGuidesChange?: (guides: Guide[]) => void;
  onContextMenu?: (id: string, x: number, y: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);
  const lastDelta = useRef({ dx: 0, dy: 0 });

  const locked = readBase(comp, 'locked', false) as boolean;
  const rotation = Number(readBase(comp, 'rotation', 0)) || 0;

  // ── 计算对齐辅助线（贴边/居中） ──
  const computeGuides = (x: number, y: number, w: number, h: number): Guide[] => {
    const guides: Guide[] = [];
    const near = (a: number, b: number) => Math.abs(a - b) <= SNAP_DIST;
    if (near(x, 0)) guides.push({ axis: 'v', pos: 0 });
    if (near(x + w, canvasSize.width)) guides.push({ axis: 'v', pos: canvasSize.width });
    if (near(x + w / 2, canvasSize.width / 2)) guides.push({ axis: 'v', pos: canvasSize.width / 2 });
    if (near(y, 0)) guides.push({ axis: 'h', pos: 0 });
    if (near(y + h, canvasSize.height)) guides.push({ axis: 'h', pos: canvasSize.height });
    if (near(y + h / 2, canvasSize.height / 2)) guides.push({ axis: 'h', pos: canvasSize.height / 2 });
    return guides;
  };

  // ── mousedown 直接拖动（缩放感知 + 边界约束 + 对齐辅助线） ──
  const handleMoveStart = (e: React.MouseEvent) => {
    if (locked || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    dragState.current = { startX: e.clientX, startY: e.clientY, originX: comp.position.x, originY: comp.position.y };
    lastDelta.current = { dx: 0, dy: 0 };
    onGuidesChange?.(computeGuides(comp.position.x, comp.position.y, comp.size.width, comp.size.height));

    const clamp = (x: number, y: number) => ({
      x: Math.max(0, Math.min(x, Math.max(0, canvasSize.width - comp.size.width))),
      y: Math.max(0, Math.min(y, Math.max(0, canvasSize.height - comp.size.height))),
    });

    const handleMove = (ev: MouseEvent) => {
      if (!dragState.current) return;
      const dx = (ev.clientX - dragState.current.startX) / scaleX;
      const dy = (ev.clientY - dragState.current.startY) / scaleY;
      lastDelta.current = { dx, dy };
      let nx = dragState.current.originX + dx;
      let ny = dragState.current.originY + dy;
      if (Math.abs(nx) <= SNAP_DIST) nx = 0;
      if (Math.abs(ny) <= SNAP_DIST) ny = 0;
      if (Math.abs(nx + comp.size.width - canvasSize.width) <= SNAP_DIST) nx = canvasSize.width - comp.size.width;
      if (Math.abs(ny + comp.size.height - canvasSize.height) <= SNAP_DIST) ny = canvasSize.height - comp.size.height;
      const c = clamp(nx, ny);
      onMove(comp.id, c.x, c.y);
      onGuidesChange?.(computeGuides(c.x, c.y, comp.size.width, comp.size.height));
    };

    const handleUp = () => {
      if (dragState.current) {
        const c = clamp(
          snapToGrid(dragState.current.originX + lastDelta.current.dx, snap),
          snapToGrid(dragState.current.originY + lastDelta.current.dy, snap),
        );
        onMove(comp.id, c.x, c.y);
      }
      dragState.current = null;
      onGuidesChange?.([]);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const handleResize = useCallback((dx: number, dy: number, corner: string) => {
    // 屏幕像素 → 设计坐标（按各自轴缩放）
    const ddx = dx / scaleX;
    const ddy = dy / scaleY;
    let newW = comp.size.width;
    let newH = comp.size.height;
    let newX = comp.position.x;
    let newY = comp.position.y;

    if (corner.includes('e')) newW = Math.max(MIN_SIZE, comp.size.width + ddx);
    if (corner.includes('w')) { newW = Math.max(MIN_SIZE, comp.size.width - ddx); newX = comp.position.x + (comp.size.width - newW); }
    if (corner.includes('s')) newH = Math.max(MIN_SIZE, comp.size.height + ddy);
    if (corner.includes('n')) { newH = Math.max(MIN_SIZE, comp.size.height - ddy); newY = comp.position.y + (comp.size.height - newH); }

    // 边界约束：不允许超出画布
    const maxX = Math.max(0, canvasSize.width - newW);
    const maxY = Math.max(0, canvasSize.height - newH);
    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));
    // 如果尺寸超出画布，也要约束
    newW = Math.min(newW, canvasSize.width);
    newH = Math.min(newH, canvasSize.height);

    onResize(comp.id, Math.round(newW), Math.round(newH));
    if (newX !== comp.position.x || newY !== comp.position.y) {
      onMove(comp.id, Math.round(newX), Math.round(newY));
    }
  }, [comp.id, comp.size, comp.position, onResize, onMove, canvasSize, scaleX, scaleY]);

  const borderRadius = readBase(comp, 'borderRadius', 4) as number;
  const borderWidth = readBase(comp, 'borderWidth', 0) as number;
  const borderColor = readBase(comp, 'borderColor', '#334155') as string;
  const boxShadow = resolveBoxShadow(comp) || undefined;
  const bgColor = readBase(comp, 'bgColor', 'transparent') as string;
  const bgImage = readBase(comp, 'bgImage', '') as string;
  const bgOpacity = readBase(comp, 'bgOpacity', 1) as number;
  const zIndex = readBase(comp, 'zIndex', 1) as number;
  const visible = readBase(comp, 'visible', true) as boolean;
  const hasRotation = rotation !== 0;
  const title = (readBase(comp, 'title', '') as string) || '';
  const titleFontSize = readBase(comp, 'titleFontSize', 14) as number;
  // 2026-10-13 修复（用户反馈"标题字体切换没有效果"）：此前未读取 titleFontFamily → 统一应用
  const titleFontFamily = readBase(comp, 'titleFontFamily', '') as string;
  const titleColor = readBase(comp, 'titleColor', '#ffffff') as string;
  const titleAlign = readBase(comp, 'titleAlign', 'center') as string;
  const titlePosition = readBase(comp, 'titlePosition', 'top') as string;
  const controlName = (readBase(comp, 'name', '') as string) || '';

  return (
    <div
      ref={ref}
      data-comp-box=""
      onMouseDown={handleMoveStart}
      onClick={(e) => {
        e.stopPropagation();
        // 阶段3｜3.3：组件点击触发联动事件（locate3dDevice/highlight3dDevice/linkGlobalDevice 等）
        makeWidgetClickHandler(comp)();
        if (!locked) onSelect(e);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu?.(comp.id, e.clientX, e.clientY);
      }}
      style={{
        display: visible ? 'block' : 'none',
        position: 'absolute',
        left: comp.position.x,
        top: comp.position.y,
        width: comp.size.width,
        height: comp.size.height,
        zIndex,
        cursor: locked ? 'default' : 'move',
        boxSizing: 'border-box',
        // overflow 必须 visible：8 点缩放手柄 absolute 负偏移在边界外，hidden 会裁剪手柄导致无法命中
        overflow: 'visible',
        borderRadius,
        border: `${borderWidth}px solid ${borderColor}`,
        boxShadow: isSelected ? `0 0 8px rgba(22,119,255,0.3)` : boxShadow,
        transform: hasRotation ? `rotate(${rotation}deg)` : undefined,
      }}
    >
      {/* 背景层（透明度只作用于背景） */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          borderRadius: Math.max(0, borderRadius - 2),
          background: bgColor,
          backgroundImage: bgImage ? `url(${bgImage})` : undefined,
          backgroundSize: bgImage ? 'cover' : undefined,
          opacity: bgOpacity,
        }}
      />
      {/* 2026-09-07｜2.1：文本组件内容层 pointerEvents 放行（否则双击事件被 none 拦截，TextWidget 双击编辑失效） */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden', borderRadius, pointerEvents: comp.type === 'text' ? 'auto' : 'none' }}>
        <ChartRenderer component={comp} />
      </div>

      {/* 标题栏：始终渲染在有 title 时，编辑态选中时显示占位提示 */}
      {(title || (isSelected && controlName)) && (
        <div
          style={{
            position: 'absolute',
            [titlePosition === 'bottom' ? 'bottom' : 'top']: 0,
            left: 0,
            right: 0,
            zIndex: 5,
            padding: '4px 10px',
            fontSize: titleFontSize,
            fontFamily: titleFontFamily || undefined,
            color: title ? titleColor : 'rgba(255,255,255,0.25)',
            textAlign: titleAlign === 'left' ? 'left' : titleAlign === 'right' ? 'right' : 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            background: title ? 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 100%)' : undefined,
            fontStyle: title ? undefined : 'italic',
          }}
        >
          {title || controlName}
        </div>
      )}

      {isSelected && !locked && (
        <>
          {/* 双层选中框：外层实线 + 内层虚线/半透明 */}
          <div style={{ position: 'absolute', inset: -2, border: `2px solid ${SELECT_COLOR}`, pointerEvents: 'none', zIndex: 6, borderRadius: borderRadius + 1 }} />
          <div style={{ position: 'absolute', inset: -1, border: `1px dashed rgba(22,119,255,0.6)`, pointerEvents: 'none', zIndex: 6, borderRadius: borderRadius + 1 }} />
          {/* 半透明遮罩 */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(22,119,255,0.05)', pointerEvents: 'none', zIndex: 6 }} />
          {/* 8 点缩放锚点 + 旋转锚点（旋转时隐藏缩放避免误操作） */}
          {!hasRotation ? (
            <>
              <ResizeHandle direction="nw" onResize={(dx, dy) => handleResize(dx, dy, 'nw')} />
              <ResizeHandle direction="n" onResize={(dx, dy) => handleResize(dx, dy, 'n')} />
              <ResizeHandle direction="ne" onResize={(dx, dy) => handleResize(dx, dy, 'ne')} />
              <ResizeHandle direction="e" onResize={(dx, dy) => handleResize(dx, dy, 'e')} />
              <ResizeHandle direction="se" onResize={(dx, dy) => handleResize(dx, dy, 'se')} />
              <ResizeHandle direction="s" onResize={(dx, dy) => handleResize(dx, dy, 's')} />
              <ResizeHandle direction="sw" onResize={(dx, dy) => handleResize(dx, dy, 'sw')} />
              <ResizeHandle direction="w" onResize={(dx, dy) => handleResize(dx, dy, 'w')} />
            </>
          ) : (
            <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: SELECT_COLOR, background: 'rgba(22,119,255,0.1)', padding: '0 6px', borderRadius: 4, zIndex: 12, pointerEvents: 'none' }}>
              {Math.round(rotation)}°
            </div>
          )}
          <RotateHandle onRotate={(deg) => onRotate(comp.id, (rotation + deg) % 360)} />
          <div
            onClick={(e) => { e.stopPropagation(); onRemove(comp.id); }}
            aria-label="删除组件"
            style={{
              position: 'absolute', top: -10, right: -10, width: 20, height: 20,
              background: '#ff4d4f', color: '#fff', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', zIndex: 20, fontSize: 12, fontWeight: 'bold',
              lineHeight: '20px', border: '2px solid #fff',
            }}
          >×</div>
        </>
      )}
      {isSelected && locked && (
        <div style={{ position: 'absolute', inset: 0, border: `1px dashed rgba(136,136,136,0.6)`, pointerEvents: 'none', zIndex: 6 }}>
          <span style={{ position: 'absolute', top: 2, right: 4, fontSize: 10, color: '#888' }}>🔒</span>
        </div>
      )}
    </div>
  );
}

interface DashboardCanvasProps {
  view: CanvasView;
  onViewChange?: (view: CanvasView) => void;
  onScaleChange?: (scale: number) => void;
  fitSignal?: number;
  snap?: number;
  showGrid?: boolean;
}

export default function DashboardCanvas({ view, onViewChange, onScaleChange, fitSignal = 0, snap = 1, showGrid = false }: DashboardCanvasProps) {
  const { components, currentPage, addComponent, updateComponent, removeComponent, reorderComponent, resizeComponent, selectComponent, selectMultiple, selectedComponentId, selectedComponentIds } =
    useDashboardStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement | null>(null);
  const canvasLayerRef = useRef<HTMLDivElement>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; compId: string | null } | null>(null);

  // 右键菜单操作
  const { duplicateComponent, setComponentLocked, moveComponentZ } = useDashboardStore();
  const closeCtx = useCallback(() => setCtxMenu(null), []);
  useEffect(() => {
    if (ctxMenu) {
      // 菜单内部 mousedown 不关闭（否则菜单项先卸载、click 丢失，功能不执行）
      const onDown = (e: Event) => {
        const t = e.target as HTMLElement | null;
        if (t && t.closest('[data-ctx-menu]')) return;
        closeCtx();
      };
      window.addEventListener('mousedown', onDown);
      window.addEventListener('scroll', onDown, true);
      return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('scroll', onDown, true); };
    }
  }, [ctxMenu, closeCtx]);

  const compLocked = (id: string) => {
    const c = components.find((x) => x.id === id);
    return !!(c?.base?.locked ?? c?.config?.locked ?? false);
  };
  const ctxCompName = (id: string) => {
    const c = components.find((x) => x.id === id);
    return c ? (c.base?.name || c.type) : '';
  };

  const pageWidth = currentPage?.config?.width ?? 1920;
  const pageHeight = currentPage?.config?.height ?? 1080;
  const scaleX = view.scaleX;
  const scaleY = view.scaleY;

  // ── 自动适应画布（供"适应"按钮使用）──
  // 画布完全填满容器：scaleX=容器宽/画布宽，scaleY=容器高/画布高（独立拉伸），组件贴边、无空隙、不越界
  const fitScale = useCallback(() => {
    const el = dropRef.current || containerRef.current;
    const parent = el?.parentElement;
    if (!parent) return;
    // 用 clientWidth/clientHeight（布局真实尺寸），避免被内部画布撑破后的 getBoundingClientRect 误导
    const width = parent.clientWidth;
    const height = parent.clientHeight;
    if (width <= 0 || height <= 0) return;
    onViewChange?.({
      scaleX: width / pageWidth,
      scaleY: height / pageHeight,
    });
    onScaleChange?.(width / pageWidth);
  }, [pageWidth, pageHeight, onViewChange, onScaleChange]);

  useEffect(() => {
    fitScale();
    const el = dropRef.current || containerRef.current;
    const parent = el?.parentElement;
    if (!parent) return;
    const ro = new ResizeObserver(fitScale);
    ro.observe(parent);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitScale]);



  // 外部“适应”按钮触发
  useEffect(() => {
    if (fitSignal > 0) fitScale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitSignal]);

  // ── Ctrl/Cmd + 滚轮缩放（向光标缩放）──
  const handleWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const f = Math.min(4, Math.max(0.2, (scaleX + scaleY) / 2 * factor) / ((scaleX + scaleY) / 2));
    onViewChange?.({
      scaleX: Math.min(4, Math.max(0.2, scaleX * f)),
      scaleY: Math.min(4, Math.max(0.2, scaleY * f)),
    });
    onScaleChange?.(scaleX * f);
  };

  // ── 从左侧控件库拖入（仅新增）──
  const [, drop] = useDrop(() => ({
    accept: ['VISUAL_COMPONENT'],
    drop: (item: any, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !containerRef.current) return;
      // A-10：付费组件无权益 → 拦截并引导开通（面板已锁定，此处兜底防程序化投放）
      const marketItem = usePluginStore.getState().marketComponents.find((c) => c.type === item.type);
      if (marketItem?.priceType === 'paid' && marketItem.entitled === false) {
        message.warning(`「${marketItem.name}」为付费组件：请开通会员 / 订阅对应套餐后使用`);
        return;
      }
      const rect = containerRef.current.getBoundingClientRect();
      const w = item.size?.width || 200;
      const h = item.size?.height || 100;
      const x = Math.max(0, Math.min((offset.x - rect.left) / scaleX, pageWidth - w));
      const y = Math.max(0, Math.min((offset.y - rect.top) / scaleY, pageHeight - h));
      addComponent({
        type: item.type,
        position: { x: snapToGrid(x, snap), y: snapToGrid(y, snap) },
        size: item.size || { width: 200, height: 100 },
        style: {},
        config: {},
      });
    },
  }), [scaleX, scaleY, snap, pageWidth, pageHeight]);

  // ── 2026-09-01｜框选（画布空白拖框，选择相交组件）──
  const [boxSel, setBoxSel] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const boxStart = useRef<{ x: number; y: number } | null>(null);
  const boxMoved = useRef(false);

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target !== canvasLayerRef.current) return;
    boxStart.current = { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
    boxMoved.current = false;
    setBoxSel({ x1: e.nativeEvent.offsetX, y1: e.nativeEvent.offsetY, x2: e.nativeEvent.offsetX, y2: e.nativeEvent.offsetY });
  };
  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (!boxStart.current || !boxSel) return;
    const nx = e.nativeEvent.offsetX, ny = e.nativeEvent.offsetY;
    if (Math.abs(nx - boxStart.current.x) > 4 || Math.abs(ny - boxStart.current.y) > 4) boxMoved.current = true;
    setBoxSel({ ...boxSel, x2: nx, y2: ny });
  };
  const handleCanvasMouseUp = () => {
    if (boxSel && boxMoved.current) {
      const x1 = Math.min(boxSel.x1, boxSel.x2), x2 = Math.max(boxSel.x1, boxSel.x2);
      const y1 = Math.min(boxSel.y1, boxSel.y2), y2 = Math.max(boxSel.y1, boxSel.y2);
      const ids = components
        .filter((c) => {
          const cx = c.position?.x ?? 0, cy = c.position?.y ?? 0;
          const cw = c.size?.width ?? 0, ch = c.size?.height ?? 0;
          return cx < x2 && cx + cw > x1 && cy < y2 && cy + ch > y1;
        })
        .map((c) => c.id);
      if (ids.length > 0) {
        useDashboardStore.setState({ selectedComponentIds: ids, selectedComponentId: ids[ids.length - 1] });
      }
    }
    boxStart.current = null;
    setBoxSel(null);
  };

  const boxRect = boxSel ? {
    left: Math.min(boxSel.x1, boxSel.x2),
    top: Math.min(boxSel.y1, boxSel.y2),
    width: Math.abs(boxSel.x2 - boxSel.x1),
    height: Math.abs(boxSel.y2 - boxSel.y1),
  } : null;

  return (
    <div
      data-testid="dashboard-canvas-drop"
      ref={(node) => {
        containerRef.current = node;
        if (node) { drop(node); dropRef.current = node; }
      }}
      onWheel={handleWheel}
      style={{
        position: 'relative',
        width: pageWidth * scaleX,
        height: pageHeight * scaleY,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <div
        ref={canvasLayerRef}
        data-testid="dashboard-canvas"
        onClick={() => { if (!boxMoved.current) selectComponent(null); }}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onContextMenu={(e) => {
          // 空白处右键：清除右键菜单（无目标组件）
          e.preventDefault();
          e.stopPropagation();
          setCtxMenu(null);
        }}
        style={{
          position: 'relative',
          width: pageWidth,
          height: pageHeight,
          transform: `scale(${scaleX}, ${scaleY})`,
          transformOrigin: '0 0',
          background: currentPage?.config?.backgroundColor || '#0f0f2e',
          backgroundImage: currentPage?.config?.backgroundImage
            ? `url(${currentPage.config.backgroundImage})`
            : undefined,
          backgroundSize: 'cover',
          overflow: 'hidden',
          outline: '1px solid rgba(255,255,255,0.18)', // 画布边界描边
        }}
      >
        {showGrid && (
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
            backgroundImage: 'linear-gradient(rgba(24,144,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(24,144,255,0.08) 1px, transparent 1px)',
            backgroundSize: '10px 10px',
          }} />
        )}
        {components.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Empty description="从左侧拖拽组件到此处" />
          </div>
        )}

        {components.map((comp) => (
          <DraggableComponent
            key={comp.id}
            comp={comp}
            isSelected={selectedComponentIds.includes(comp.id)}
            scaleX={scaleX}
            scaleY={scaleY}
            snap={snap}
            canvasSize={{ width: pageWidth, height: pageHeight }}
            onSelect={(e) => {
              // 2026-09-01｜Ctrl/⌘ + 点击 = 多选（toggle）
              if (e.ctrlKey || e.metaKey) selectMultiple(comp.id);
              else selectComponent(comp.id);
            }}
            onMove={(id, x, y) => reorderComponent(id, { x, y })}
            onResize={(id, w, h) => resizeComponent(id, { width: w, height: h })}
            onContextMenu={(id, x, y) => {
              selectComponent(id);
              setCtxMenu({ x, y, compId: id });
            }}
            onRotate={(id, deg) => updateComponent(id, { config: { ...(comp.config || {}), rotation: deg } })}
            onRemove={(id) => removeComponent(id)}
            onGuidesChange={setGuides}
          />
        ))}

        {/* 对齐辅助线 */}
        {guides.map((g, i) => (
          <div
            key={i}
            style={{
              position: 'absolute', zIndex: 9996, pointerEvents: 'none',
              background: '#40a9ff',
              ...(g.axis === 'v'
                ? { left: g.pos, top: 0, width: 1, height: '100%', boxShadow: '0 0 4px rgba(64,169,255,0.8)' }
                : { top: g.pos, left: 0, height: 1, width: '100%', boxShadow: '0 0 4px rgba(64,169,255,0.8)' }),
            }}
          />
        ))}

        {/* 2026-09-01｜框选区域（多选） */}
        {boxRect && (
          <div
            style={{
              position: 'absolute', zIndex: 9995, pointerEvents: 'none',
              left: boxRect.left, top: boxRect.top,
              width: boxRect.width, height: boxRect.height,
              border: '1px solid #40a9ff', background: 'rgba(64,169,255,0.1)',
            }}
          />
        )}
      </div>

      {/* 右键菜单：Portal 渲染到 body，脱离画布 transform:scale 作用域，避免 fixed 定位错位/点击失效 */}
      {ctxMenu &&
        createPortal(
          <div data-ctx-menu="1" style={{
            position: 'fixed',
            left: ctxMenu.x,
            top: ctxMenu.y,
            zIndex: 100000,
            minWidth: 160,
            background: '#1a1a2e',
            border: '1px solid #333',
            borderRadius: 6,
            boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
            padding: '4px 0',
            userSelect: 'none',
          }} onContextMenu={(e) => e.preventDefault()}>
            {ctxMenu.compId ? (
              <>
                <CtxItem icon="复制" label={`复制「${ctxCompName(ctxMenu.compId)}」`} onClick={() => { duplicateComponent(ctxMenu.compId!); closeCtx(); }} />
                <CtxItem icon={compLocked(ctxMenu.compId) ? '🔓' : '🔒'} label={compLocked(ctxMenu.compId) ? '解除锁定' : '锁定'} onClick={() => { setComponentLocked(ctxMenu.compId!, !compLocked(ctxMenu.compId!)); closeCtx(); }} />
                <div style={{ borderTop: '1px solid #2a2a3e', margin: '4px 0' }} />
                <CtxItem icon="置顶" label="置顶图层" onClick={() => { moveComponentZ(ctxMenu.compId!, 'top'); closeCtx(); }} />
                <CtxItem icon="置底" label="置底图层" onClick={() => { moveComponentZ(ctxMenu.compId!, 'bottom'); closeCtx(); }} />
                <CtxItem icon="↑" label="上移一层" onClick={() => { moveComponentZ(ctxMenu.compId!, 'up'); closeCtx(); }} />
                <CtxItem icon="↓" label="下移一层" onClick={() => { moveComponentZ(ctxMenu.compId!, 'down'); closeCtx(); }} />
                <div style={{ borderTop: '1px solid #2a2a3e', margin: '4px 0' }} />
                <CtxItem icon="删除" label="删除组件" danger onClick={() => { removeComponent(ctxMenu.compId!); closeCtx(); }} />
              </>
            ) : (
              <CtxItem icon="粘贴" label="粘贴（复制后可用）" disabled />
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

function CtxItem({ icon, label, onClick, danger, disabled }: { icon: string; label: string; onClick?: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <div
      onClick={() => !disabled && onClick?.()}
      style={{
        padding: '6px 12px', fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer',
        color: danger ? '#ff4d4f' : '#e0e0e0', opacity: disabled ? 0.4 : 1,
        display: 'flex', alignItems: 'center', gap: 8,
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#2a2a3e'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
    >
      <span style={{ fontSize: 12, width: 14, textAlign: 'center' }}>{icon}</span>
      {label}
    </div>
  );
}
